/* 0.49.19 - Going to an element of a page by its Element ID: an address
 * ending in #<element_id> (/hy/p/cabinet-main#title_trainings) scrolls
 * to that element once the page has drawn it. Works on opening the
 * address, on a link to it from another page, and on a link within the
 * same page. The element may appear late (an API table, a library), so
 * it is looked for for a few seconds, and once found it is scrolled to
 * again a moment later, after pictures and data above it have taken
 * their place. Elements are found by data-element-id, which every
 * element with an Element ID carries. */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { scrollToTarget } from '../utils/scrollToTarget';

const ID_RE = /^[A-Za-z0-9_.-]{1,120}$/;

export function scrollToHashTarget(hash) {
  let raw = String(hash || '').replace(/^#/, '');
  try { raw = decodeURIComponent(raw); } catch (_e) { /* keep raw */ }
  if (!ID_RE.test(raw)) return () => {};
  let tries = 0;
  let timer = null;
  let again = null;
  const sel = `[data-element-id="${raw}"]`;
  const tick = () => {
    if (document.querySelector(sel)) {
      scrollToTarget('element', raw);
      again = setTimeout(() => scrollToTarget('element', raw), 700);
      return;
    }
    tries += 1;
    if (tries < 50) timer = setTimeout(tick, 100);
  };
  timer = setTimeout(tick, 50);
  return () => { clearTimeout(timer); clearTimeout(again); };
}

/* `ready`: the page has its content (re-runs when it arrives). */
export default function useHashScroll(ready) {
  const location = useLocation();
  useEffect(() => {
    if (!ready || !location.hash) return undefined;
    return scrollToHashTarget(location.hash);
  }, [location.hash, location.pathname, ready]);
  // A plain #link on the page (not through the router) changes only the hash.
  useEffect(() => {
    if (!ready) return undefined;
    let stop = () => {};
    const on = () => { stop(); stop = scrollToHashTarget(window.location.hash); };
    window.addEventListener('hashchange', on);
    return () => { window.removeEventListener('hashchange', on); stop(); };
  }, [ready]);
}
